// Signs the user out after a stretch of inactivity.
//
// Any real interaction (mouse, keyboard, touch, scroll, or coming back to the
// tab) counts as activity and restarts the clock. The deadline is kept in
// sessionStorage as a timestamp rather than a plain setTimeout, so a laptop that
// was asleep past the limit is signed out the moment it wakes rather than being
// granted a fresh window.
import { useEffect, useRef } from 'react';

const DEADLINE_KEY = 'cysops.idleDeadline';
export const SIGNOUT_REASON_KEY = 'cysops.signedOutReason';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'wheel', 'click'];
const CHECK_MS = 15 * 1000;   // how often the deadline is compared to the clock
const THROTTLE_MS = 5 * 1000; // don't rewrite the deadline more than this often

// Returns a ref holding the timestamp of the last interaction, so callers can
// tell "still working" from "walked away" (used for the server-side heartbeat).
export function useIdleLogout(active, minutes, onTimeout) {
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    if (!active) {
      try { sessionStorage.removeItem(DEADLINE_KEY); } catch { /* ignore */ }
      return undefined;
    }

    const limitMs = Math.max(1, minutes) * 60 * 1000;
    let lastWrite = 0;
    let firedAlready = false;

    const bump = (force = false) => {
      const now = Date.now();
      lastActivityRef.current = now;
      if (!force && now - lastWrite < THROTTLE_MS) return;
      lastWrite = now;
      try { sessionStorage.setItem(DEADLINE_KEY, String(now + limitMs)); } catch { /* ignore */ }
    };

    const expired = () => {
      let deadline = Number(sessionStorage.getItem(DEADLINE_KEY) || 0);
      if (!deadline) { bump(true); deadline = Date.now() + limitMs; }
      return Date.now() > deadline;
    };

    const check = () => {
      if (firedAlready) return;
      let out = false;
      try { out = expired(); } catch { out = false; }
      if (!out) return;
      firedAlready = true;
      try {
        sessionStorage.removeItem(DEADLINE_KEY);
        sessionStorage.setItem(SIGNOUT_REASON_KEY, 'idle');
      } catch { /* ignore */ }
      onTimeoutRef.current?.();
    };

    // A reload shouldn't extend an already-blown deadline, so check before bumping.
    check();
    bump(true);

    const onActivity = () => bump();
    const onVisible = () => { check(); if (document.visibilityState === 'visible') bump(true); };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(check, CHECK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [active, minutes]);

  return lastActivityRef;
}
